import React, { useEffect, useRef } from "react";

import { z } from "zod";

import { Icon } from "@braze/beacon-core";
import { useId } from "@braze/beacon-hooks";

import { useAgenticAiContext } from "@services/useAgenticAiContext";
import { useCanUseOperator } from "@services/useCanUseOperator";
import { useFeatureOn } from "@services/useFeatureOn";
import useI18nNamespace from "@services/useI18nNamespace";
import { useOperatorAgenticChangeEnabled } from "@services/useOperatorAgenticChangeEnabled";

import { BrazeOperatorLaunchButtonWithFeatureFlipper } from "@components/AgenticAi/components/BrazeOperatorLaunchButtonWithFeatureFlipper";
import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay/DiffDisplay";

import { StyledBrazeModal, StyledOperatorLaunchButtonWrapper } from "./BrazeModal.style";
import type { BrazeModalProps } from "./types";

export const BrazeModal = ({
  isOpen,
  onOperatorClose,
  operatorLabel,
  hideOperatorButton = false,
  children,
  size,
  ignoreOutsideClick,
  toggle,
  hideCloseButton,
  ...props
}: BrazeModalProps): React.ReactElement => {
  const { t } = useI18nNamespace("braze-modal");
  const { registerContext, registerTool, isOpen: isOperatorChatOpen } = useAgenticAiContext();
  const canUseOperator = useCanUseOperator();
  const featureOn = useFeatureOn();
  const isAgenticChangeEnabled = useOperatorAgenticChangeEnabled();
  const id = useId("braze-modal");

  const onOperatorCloseRef = useRef(onOperatorClose);
  onOperatorCloseRef.current = onOperatorClose;

  useEffect(() => {
    if (!isOpen || !isAgenticChangeEnabled) {
      return;
    }

    const cleanup = registerTool({
      toolEntityId: id,
      name: "close_modal",
      description: "Close a modal",
      parameters: z.object({}),
      callback: () => {
        onOperatorCloseRef.current();
        return "Modal was successfully closed.";
      },
      options: {
        toolGroup: "Other",
        labels: {
          ready: t("close-modal-ready", "Preparing to close modal"),
          running: t("close-modal-running", "Closing modal"),
          complete: t("close-modal-complete", "Modal closed"),
          failed: t("close-modal-failed", "Failed to close modal"),
          stopped: t("close-modal-stopped", "Stopped closing modal"),
        },
        requiresApproval: "yes",
        argumentsSummaryText: () => operatorLabel,
        renderApproval: () => {
          return (
            <DiffDisplay
              title={t("action-title", "Action")}
              modified={t("close-modal-description", "Close modal: {label}", { label: operatorLabel })}
            />
          );
        },
        toolIcon: <Icon name="times" />,
      },
    });

    return () => {
      cleanup();
    };
  }, [registerTool, id, t, isOpen, operatorLabel, isAgenticChangeEnabled]);

  useEffect(() => {
    if (!isOpen || !canUseOperator || !isAgenticChangeEnabled) {
      return;
    }

    return registerContext("braze_modal", "", { ignoreDuplicates: true });
  }, [canUseOperator, isOpen, registerContext, isAgenticChangeEnabled]);

  return (
    <StyledBrazeModal
      isOpen={isOpen}
      size={size}
      ignoreOutsideClick={ignoreOutsideClick || isOperatorChatOpen}
      toggle={toggle}
      hideCloseButton={hideCloseButton}
      {...props}
      data-operator-id={id}
    >
      {featureOn("sage_ai_operator_button_in_modal") && !hideOperatorButton && isAgenticChangeEnabled && (
        <StyledOperatorLaunchButtonWrapper $size={size} $offsetForCloseIcon={Boolean(toggle) && !hideCloseButton}>
          <BrazeOperatorLaunchButtonWithFeatureFlipper />
        </StyledOperatorLaunchButtonWrapper>
      )}
      {children}
    </StyledBrazeModal>
  );
};
